/**
 * paceModel — pure pace / gap-to-target from the §5 resolve envelope (A3 module 2).
 * No fetch (CD-7): reuses the page's single /resolve, sliced to one site. Method is
 * LINEAR run-rate and it is stated on the surface (seasonalised projection is a later
 * beat, never a silent default). Honest nulls: an unserved month is a gap in the pace
 * basis (excluded + named), never interpolated to keep the projection "clean". The
 * gap is framed by the metric's direction so "behind" is correct per metric.
 */

import type {
  Cadence,
  CatalogueResponse,
  Cell,
  Direction,
  GroupRule,
  MetricFormat,
  RegistryMetric,
  ResolveResponse,
  Site,
} from '../../types/metrics'

const YEAR_MONTHS = 12 // linear projection horizon (pace is a monthly, calendar-year concept)

export interface PaceModel {
  metricId: string
  title: string
  format: MetricFormat
  direction: Direction
  basis: 'sum' | 'average'
  monthsServed: number
  monthsMissing: string[] // window periods with no served Actual — honest gaps
  ytdActual: number | null // raw (Σ for flows, mean for rates)
  ytdTarget: number | null
  projection: number | null // raw linear year-end at current trajectory
  annualTarget: number | null // raw
  gap: number | null // projection − annualTarget (raw)
  ahead: boolean | null // direction-aware: is the projection meeting/beating target
  method: string
}

const isFlow = (rule: GroupRule | null): boolean => rule === 'sum'

function servedValues(resolve: ResolveResponse, metricId: string, site: Site, plan: 'Actual' | 'Target') {
  const byPeriod = new Map<string, number>()
  for (const cell of resolve.cells) {
    if (cell.metric_id === metricId && cell.site === site && cell.plan_type === plan && cell.value !== null) {
      byPeriod.set(cell.period, cell.value)
    }
  }
  return byPeriod
}

/** Σ for a flow, mean for a rate; null when nothing served (never a zero). */
function aggregate(values: number[], basis: 'sum' | 'average'): number | null {
  if (values.length === 0) return null
  const total = values.reduce((sum, v) => sum + v, 0)
  return basis === 'sum' ? total : total / values.length
}

function project(ytd: number | null, monthsServed: number, basis: 'sum' | 'average'): number | null {
  if (ytd === null || monthsServed === 0) return null
  return basis === 'sum' ? (ytd / monthsServed) * YEAR_MONTHS : ytd // a rate's run-rate is flat
}

function isAhead(projection: number | null, target: number | null, direction: Direction): boolean | null {
  if (projection === null || target === null) return null
  return direction === 'higher_is_better' ? projection >= target : projection <= target
}

export function buildPace(resolve: ResolveResponse, metric: RegistryMetric, site: Site): PaceModel {
  const basis: 'sum' | 'average' = isFlow(metric.group_rule) ? 'sum' : 'average'
  const actuals = servedValues(resolve, metric.id, site, 'Actual')
  const targets = servedValues(resolve, metric.id, site, 'Target')
  const monthsMissing = resolve.periods.filter((period) => !actuals.has(period))
  const ytdActual = aggregate([...actuals.values()], basis)
  const ytdTarget = aggregate([...targets.values()], basis)
  const projection = project(ytdActual, actuals.size, basis)
  const annualTarget = project(ytdTarget, targets.size, basis)
  const gap = projection !== null && annualTarget !== null ? projection - annualTarget : null
  const method =
    basis === 'sum'
      ? `Linear run-rate — projection = YTD ÷ ${actuals.size} served month${actuals.size === 1 ? '' : 's'} × ${YEAR_MONTHS}; target annualised the same way`
      : 'Run-rate = mean of served months (flat projection for a rate, not seasonalised)'
  return {
    metricId: metric.id,
    title: metric.title,
    format: metric.format,
    direction: metric.direction,
    basis,
    monthsServed: actuals.size,
    monthsMissing,
    ytdActual,
    ytdTarget,
    projection,
    annualTarget,
    gap,
    ahead: isAhead(projection, annualTarget, metric.direction),
    method,
  }
}

/** The P&L money-flow metrics pace is meaningful for — registry-driven, no id
 *  dispatch (CD-4). Restricted to the Profit/Cost pillars: those gbp_k+sum metrics
 *  are period flows that annualise, whereas a gbp_k+sum metric in another pillar
 *  (e.g. past_due, a Delivery period-end BALANCE) is a stock — summing its snapshots
 *  into a YTD and projecting ×12 would be meaningless. group_rule=sum is a SPATIAL
 *  rule (Group = Σ sites), not temporal additivity, so the pillar is the flow signal.
 *  A dedicated registry flow attribute would be the fuller fix (see feedback). */
const FLOW_PILLARS = new Set(['Profit', 'Cost'])

export function pickPaceMetrics(catalogue: CatalogueResponse, cadence: Cadence): RegistryMetric[] {
  return catalogue.pillars.flatMap((group) =>
    group.metrics.filter(
      (metric) =>
        metric.matrix &&
        FLOW_PILLARS.has(metric.pillar) &&
        metric.format === 'gbp_k' &&
        metric.group_rule === 'sum' &&
        metric.cadence.includes(cadence)
    )
  )
}
